import React from 'react';
import { CreditCard, Calendar, Percent, Coins } from 'lucide-react';

const BasicParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  cryptocurrencyTypes,
  investmentCurrencies
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Сумма инвестиции */}
      <div>
        <label htmlFor="initialInvestment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <CreditCard className="w-4 h-4 mr-2 text-indigo-500" />
          Сумма инвестиции
        </label>
        <div className="flex">
          <input
            type="text"
            id="initialInvestment"
            name="initialInvestment"
            value={formData.initialInvestment}
            onChange={handleInputChange}
            className={`form-input w-full rounded-r-none ${errors.initialInvestment ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          <select
            name="investmentCurrency"
            value={formData.investmentCurrency}
            onChange={handleInputChange}
            className="form-select rounded-l-none w-1/4 border-l-0"
          >
            {investmentCurrencies.map((currency) => (
              <option key={currency.id} value={currency.id}>
                {currency.symbol}
              </option>
            ))}
          </select>
        </div>
        {errors.initialInvestment && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.initialInvestment}
          </p>
        )}
      </div>

      {/* Тип криптовалюты */}
      <div>
        <label htmlFor="cryptocurrencyType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Coins className="w-4 h-4 mr-2 text-indigo-500" />
          Криптовалюта
        </label>
        <select
          id="cryptocurrencyType"
          name="cryptocurrencyType"
          value={formData.cryptocurrencyType}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.cryptocurrencyType ? 'input-error' : ''}`}
        >
          {cryptocurrencyTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        {errors.cryptocurrencyType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.cryptocurrencyType}
          </p>
        )}
      </div>

      {/* Срок инвестирования */}
      <div>
        <label htmlFor="investmentPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
          Срок инвестирования (месяцев)
        </label>
        <input
          type="text"
          id="investmentPeriod"
          name="investmentPeriod"
          value={formData.investmentPeriod}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.investmentPeriod ? 'input-error' : ''}`}
          placeholder="Введите срок"
        />
        {errors.investmentPeriod && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.investmentPeriod}
          </p>
        )}
      </div>

      {/* Ожидаемый рост */}
      <div>
        <label htmlFor="expectedGrowthRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Percent className="w-4 h-4 mr-2 text-indigo-500" />
          Ожидаемый рост в месяц (%)
        </label>
        <input
          type="text"
          id="expectedGrowthRate"
          name="expectedGrowthRate"
          value={formData.expectedGrowthRate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.expectedGrowthRate ? 'input-error' : ''}`}
          placeholder="Введите ожидаемый рост"
        />
        {errors.expectedGrowthRate && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.expectedGrowthRate}
          </p>
        )}
      </div>

      {/* Комиссии */}
      <div>
        <label htmlFor="feePercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Комиссии биржи/обменника (%)
        </label>
        <input
          type="text"
          id="feePercentage"
          name="feePercentage"
          value={formData.feePercentage}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.feePercentage ? 'input-error' : ''}`}
          placeholder="Введите комиссию"
        />
        {errors.feePercentage && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.feePercentage}
          </p>
        )}
      </div>

      {/* Реинвестирование */}
      <div className="col-span-full">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="reinvestProfit"
              name="reinvestProfit"
              type="checkbox"
              checked={formData.reinvestProfit}
              onChange={handleInputChange}
              className="form-checkbox"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="reinvestProfit" className="font-medium text-gray-700 dark:text-gray-300">
              Реинвестировать часть прибыли
            </label>
            <p className="text-gray-500 dark:text-gray-400">
              Автоматическое реинвестирование части прибыли обратно в криптовалюту
            </p>
          </div>
        </div>
      </div>

      {/* Процент реинвестирования (если активно) */}
      {formData.reinvestProfit && (
        <div className="col-span-full">
          <label htmlFor="reinvestmentPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Процент прибыли для реинвестирования
          </label>
          <input
            type="range"
            id="reinvestmentPercentage"
            name="reinvestmentPercentage"
            value={formData.reinvestmentPercentage}
            onChange={handleInputChange}
            min="0"
            max="100"
            step="5"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{formData.reinvestmentPercentage}%</span>
            <span>100%</span>
          </div>
          {errors.reinvestmentPercentage && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.reinvestmentPercentage}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BasicParametersSection;